import { path } from 'ramda'
import { useEffect, useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect } from 'react-router-dom'
import {
  Box,
  Card,
  Text,
  Stack,
  Button,
  TextLinkButton,
  useDialogControllers,
  Dropdown
} from '@machobear/design-system'
import { breakpoints } from '@machobear/design-system/css'
import config from '@/config'
import { useToaster } from '@/models/toaster/hooks'
import useMediaQuery from '@/hooks/use-media-query'
import useProfile from '@/hooks/use-profile'
import { useListingShareButtonClicked } from '@/hooks/use-listing-share-button-clicked'
import {
  IdVerificationButton,
  personaListingVerificationClassified,
  personaListingVerificationUnclassified
} from '@/features/Persona'
import { isNullOrEmpty } from '@/utils'
import { DefaultButtonBar } from '@/components/button-bar'
import Page from '@/components/page'
import PageTitle from '@/components/page-title'
import { VerifiedBadge } from '@/components/icons'
import { pushToDatalayer } from '@/utils/push-datalayer'
import VerifyCodeModal from './verify-code-modal'
import VerifyAddressModal from './verify-address-modal'
import UploadButton from './upload-button'
import { show as showSampleDocumentModal } from './sample-document-modal'
import MaxAttemptsModal from './max-attempts-modal'
import VerificationSuccessModal from './verification-success-modal'

const HeadingTemplate = ({ step, heading }) => {
  return (
    <Box display="flex" flexDirection="column" alignItems="flexStart" gap="xsmall">
      <Text size="small" weight="medium" tone="tertiary">
        {step}
      </Text>
      <Text size="large" weight="strong" tone="neutral">
        {heading}
      </Text>
    </Box>
  )
}

const CardTemplate = ({ button, headingText, descriptionText, footerText, isMobile, badge }) => {
  return (
    <Card rounded={true}>
      <Box
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        gap={isMobile ? 'gutter' : 'xxlarge'}
        alignItems="center"
        borderRadius="large"
      >
        <Box style={{ width: isMobile ? '100%' : 388 }}>
          <Stack space="small">
            <Box display="flex" flexDirection="row" alignItems="center" gap="xxxsmall">
              <Text size="standard" weight="strong" tone="neutral">
                {headingText}
              </Text>
              {badge && <VerifiedBadge />}
            </Box>
            <Text size="small" weight="regular" tone="secondary">
              {descriptionText}
            </Text>
            {footerText && (
              <Box paddingTop="xsmall">
                <Text size="xsmall" weight="strong" tone="secondary">
                  <em>{footerText}</em>
                </Text>
              </Box>
            )}
          </Stack>
        </Box>
        <Box display="flex" style={{ width: isMobile ? '100%' : 160, height: '100%' }}>
          {button}
        </Box>
      </Box>
    </Card>
  )
}

const VerifyListing = ({
  match: {
    params: { listingId }
  },
  history
}) => {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const listingData = useSelector(path(['listing', listingId]))
  const typesRequested = listingData?.listings?.verified_request_types || []
  const documentSubmitted = typesRequested.includes('file_upload')
  const postcardSent = typesRequested.includes('mail')
  const profile = useProfile()
  const isLoadingProfile = isNullOrEmpty(profile.id)
  const toaster = useToaster()
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.tablet}px)`)

  const isProd = config.BUILD_ENV === 'prod'
  const [verifyDocType, setVerifyDocType] = useState('')
  const isClassifiedDocType = ['utility', 'assessment'].includes(verifyDocType)
  const personaModalData = {
    listingId,
    city: listingData?.building?.city,
    state: listingData?.building?.state,
    countryCode: listingData?.building?.country_code,
    streetAddress: listingData?.building?.full_street_name,
    unitNumber: listingData?.unit?.unit_number,
    postalCode: listingData?.building?.zip,
    firstName: profile.first_name,
    lastName: profile.last_name,
    language: i18n.languages[0]
  }

  const { visible, showModal, closeModal } = useDialogControllers({
    verifyAddress: false,
    verifyCode: false,
    maxAttempts: false,
    verificationSuccess: false
  })

  useEffect(() => {
    dispatch.listing.load(listingId)
  }, [dispatch.listing, listingId])

  const state = path(['tasks', 'publish_listing', 'steps', 'verify', 'state'])(toaster)
  const updateState = toaster.updateState
  useEffect(() => {
    if (state !== 'incomplete') return
    updateState('publish_listing.verify', {
      step: {
        state: 'complete'
      }
    })
  }, [updateState, state])

  const shareButtonClicked = useListingShareButtonClicked(listingId)
  const handleSuccessEvent = () => {
    pushToDatalayer({ event: 'app_listdashboard_status_verify_complete' })
    if (shareButtonClicked) {
      pushToDatalayer({ event: 'app_listdashboard_status_all_complete' })
    }
  }

  if (!listingData) return null

  // Redirect to listing dashboard if listing is already verified
  const isListingVerified = listingData?.listings?.verified_state_machine === 'approved'
  if (isListingVerified) {
    return <Redirect to={`/listing/${listingId}?verified=1`} replace />
  }

  const DocumentText = () => {
    return documentSubmitted ? (
      t(
        'l.verify_listing.view.proof_document_submitted.text',
        `We're busy verifying your document. Thanks for your patience!`
      )
    ) : (
      <>
        {t(
          'l.verify_listing.view.upload_proof_document.text',
          'Upload one of the following: land title document, property tax notice, utility bills or contract of purchase and sale.'
        )}{' '}
        <TextLinkButton onClick={showSampleDocumentModal}>
          {t('l.listing.verify.proof_document.view_example', 'View example')}
        </TextLinkButton>
      </>
    )
  }

  const DocumentButton = () => {
    return documentSubmitted ? (
      <Button variant="solid" size="standard" disabled>
        {t('l.verify_listing.view.pending_approval.button', 'Pending Approval')}
      </Button>
    ) : (
      <UploadButton listingId={listingId} handleSuccessEvent={handleSuccessEvent} />
    )
  }

  const mailText = postcardSent
    ? t(
        'l.verify_listing.view.postcard_sent.text',
        'A postcard is on the way! (can take 5-10 business days) Enter the 6-digit verification code.'
      )
    : t(
        'l.verify_listing.view.request_verify_by_mail.text',
        `Receive a verification postcard to this listing's address. This can take 5-10 business days.`
      )

  const MailButton = () => {
    return postcardSent ? (
      <Button variant="solid" size="standard" onClick={showModal('verifyCode')}>
        {t('l.verify_listing.view.enter_code.button', 'Enter Code')}
      </Button>
    ) : (
      <Button variant="solid" size="standard" onClick={showModal('verifyAddress')}>
        {t('l.verify_listing.view.request.button', 'Request')}
      </Button>
    )
  }

  return (
    <>
      <Page>
        <PageTitle>{t('l.verify_listing.view.verify_listing.title', 'Verify Listing')}</PageTitle>
        <Box
          display="flex"
          paddingY="xxlarge"
          paddingX={isMobile ? 'small' : 'xlarge'}
          flexDirection="column"
          alignItems="center"
          gap="xxlarge"
        >
          <Box display="flex" flexDirection="column" gap="gutter" maxWidth="small" width="full">
            <HeadingTemplate
              step={t('l.listing.verify.step_1.text', 'Step 1')}
              heading={t('l.listing.verify.verify_your_identity.text', 'Verify your identity')}
            />
            <CardTemplate
              button={<IdVerificationButton disabled={isLoadingProfile} />}
              headingText={t('l.listing.verify.verify_id.text', 'Verify ID')}
              descriptionText={t(
                'l.listing.verify.match_id.text',
                "We'll match your legal name with your ID and listing verification documents."
              )}
              isMobile={isMobile}
              badge
            />
          </Box>
          <Box display="flex" flexDirection="column" gap="gutter" maxWidth="small" width="full">
            <HeadingTemplate
              step={t('l.listing.verify.step_2.text', 'Step 2')}
              heading={t(
                'l.verify_listing.view.verify_your_listing.title',
                'Choose one method to verify your listing'
              )}
            />
            <CardTemplate
              button={<DocumentButton />}
              headingText={t(
                'l.verify_listing.view.proof_document.label',
                'Verify by Proof Document'
              )}
              descriptionText={<DocumentText />}
              footerText={t(
                'l.verify_listing.view.upload_proof_document_disclaimer.text',
                'The name on this document should match your submitted ID'
              )}
              isMobile={isMobile}
            />
            <CardTemplate
              button={<MailButton />}
              headingText={t('l.verify_listing.view.verify_by_mail.label', 'Verify by Mail')}
              descriptionText={mailText}
              isMobile={isMobile}
            />
          </Box>
          <Box>
            <Text size="small" weight="regular" tone="secondary" align="center">
              <Trans i18nKey="l.verify_listing.view.postcard_sent_contact_us.text">
                Contact <a href="mailto:support@liv.rent">support@liv.rent</a> if you need help.
              </Trans>
            </Text>
          </Box>
          {/* PERSONA LISTING VERIFICATION - DEV ONLY */}
          {!isProd && (
            <Box
              display="flex"
              flexDirection="column"
              gap="gutter"
              maxWidth="small"
              width="full"
              paddingBottom="xxlarge"
              marginBottom="xxlarge"
            >
              <HeadingTemplate step="DEV ONLY" heading="Persona Listing Verification" />
              <Dropdown
                label="Select document type"
                value={verifyDocType}
                onChange={v => setVerifyDocType(v)}
                searchable={false}
                options={[
                  { label: 'Utility Bill', value: 'utility' },
                  { label: 'Property Notice of Assessment', value: 'assessment' },
                  { label: 'Property Tax Bill', value: 'property_tax' },
                  { label: 'Property Title Deed', value: 'title' },
                  { label: 'Purchase Agreement/Contract', value: 'contract' },
                  { label: 'Property Transfer Agreement', value: 'transfer' },
                  { label: 'Other', value: 'other' }
                ]}
              />
              <CardTemplate
                button={
                  <Button
                    variant="solid"
                    size="standard"
                    disabled={!verifyDocType}
                    onClick={() =>
                      isClassifiedDocType
                        ? personaListingVerificationClassified(personaModalData)
                        : personaListingVerificationUnclassified(personaModalData)
                    }
                  >
                    Verify Listing
                  </Button>
                }
                headingText="Listing Verification"
                descriptionText={
                  !verifyDocType ? (
                    'Select a document type'
                  ) : (
                    <span>
                      Persona template:{' '}
                      <strong>{isClassifiedDocType ? 'Classified' : 'Unclassified'}</strong>
                    </span>
                  )
                }
                isMobile={isMobile}
              />
            </Box>
          )}
        </Box>
      </Page>
      <DefaultButtonBar handleBack={() => history.replace(`/listing/${listingId}`)} />
      <VerifyAddressModal
        listingData={listingData}
        onGetSupport={() => history.push('/chat/support-room')}
        handleSuccessEvent={handleSuccessEvent}
        open={visible.verifyAddress}
        onClose={closeModal('verifyAddress')}
      />
      <VerifyCodeModal
        listingId={listingId}
        history={history}
        showModal={showModal}
        open={visible.verifyCode}
        onClose={closeModal('verifyCode')}
      />
      <MaxAttemptsModal
        listingId={listingId}
        history={history}
        open={visible.maxAttempts}
        onClose={closeModal('maxAttempts')}
      />
      <VerificationSuccessModal
        listingId={listingId}
        history={history}
        open={visible.verificationSuccess}
        onClose={closeModal('verificationSuccess')}
      />
    </>
  )
}

export default VerifyListing
